/**
 * ADR-0095: Watermark State section (runtime view).
 *
 * Renders below Config when:
 *   * persist has a keyed policy
 *   * watermark binding configured
 *   * pdoId !== null
 *
 * Surfaces:
 *   * Orphan warning banner (when binding_orphaned)
 *   * Mode banner (red INIT / green INCREMENTAL)
 *   * State fields (current value, strategy, "Next pull will be" predicate)
 *   * Action buttons (Switch mode, Set watermark, Run now)
 *   * History disclosure (last 100 audit entries; resolves actor display names)
 *
 * Polls `/tenant/pdos/:id/watermark` on mount and after every state-modifying action.
 */

import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../../lib/api.js";
import { useToast } from "../../Toast.js";
import { useConfirm } from "../../ConfirmDialog.js";

export type WatermarkState = {
  pdo_id: string;
  variable_name: string;
  variable_type: "string" | "int" | "decimal" | "date" | "datetime";
  mode: "initialize" | "increment";
  current_value: string;
  strategy: Record<string, unknown> | null;
  last_advanced_at: string | null;
  last_advance_from: string | null;
  last_advance_to: string | null;
  next_predicate_preview: string;
  last_run_advance_error: string | null;
  binding_orphaned: boolean;
};

export type WatermarkAuditEntry = {
  id: string;
  occurred_at: string;
  kind: "mode_flip" | "manual_override" | "advance" | "binding_added" | "binding_changed" | "binding_removed";
  old_value: string | null;
  new_value: string | null;
  actor_display_name: string;
  reason: string | null;
};

function strategyLabel(strategy: WatermarkState["strategy"]): string {
  if (!strategy) return "—";
  const kind = strategy.kind as string | undefined;
  if (kind === "max_of_column") {
    const col = String(strategy.column ?? "");
    const bv = strategy.buffer_value;
    const bu = strategy.buffer_unit;
    if (typeof bv === "number" && typeof bu === "string" && bv > 0) {
      return `max(${col}) − ${bv}${bu[0]}`;
    }
    return `max(${col})`;
  }
  if (kind === "last_run_time") {
    const bv = strategy.buffer_value;
    const bu = strategy.buffer_unit;
    if (typeof bv === "number" && typeof bu === "string" && bv > 0) {
      return `last successful run time − ${bv}${bu[0]}`;
    }
    return "last successful run time";
  }
  if (kind === "formula") {
    return `formula: ${String(strategy.expression ?? "")}`;
  }
  return "—";
}

function formatAuditKindLabel(kind: WatermarkAuditEntry["kind"]): string {
  switch (kind) {
    case "mode_flip":       return "Mode changed";
    case "manual_override": return "Watermark set manually";
    case "advance":         return "Watermark advanced";
    case "binding_added":   return "Binding added";
    case "binding_changed": return "Binding changed";
    case "binding_removed": return "Binding removed";
  }
}

function formatAuditFromTo(entry: WatermarkAuditEntry): string {
  switch (entry.kind) {
    case "mode_flip":
      return `${(entry.old_value ?? "").toUpperCase()} → ${(entry.new_value ?? "").toUpperCase()}`;
    case "manual_override":
    case "advance":
      return `${entry.old_value ?? "—"} → ${entry.new_value ?? "—"}`;
    case "binding_added":
    case "binding_changed":
    case "binding_removed":
      return `${entry.old_value ?? "—"} → ${entry.new_value ?? "—"}`;
  }
}

// Absolute timestamps only — relative ("5m ago") is never acceptable in
// Atlas UI (standing user rule).
function absoluteTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function WatermarkStateSection({
  pdoId,
  pdoName,
  hasRunningMaterialization,
  onRunNow,
  refreshNonce,
  onModeChanged,
  onPreview,
  previewBusy,
  children,
}: {
  pdoId: string;
  /** Used as the typed-confirm string for the destructive "Switch to INIT mode" action. */
  pdoName: string;
  hasRunningMaterialization: boolean;
  onRunNow: () => void | Promise<void>;
  /** Bumped by the parent after a run; re-fetches this section's state so the
   *  mode pill reflects the post-INIT flip to increment. */
  refreshNonce?: number;
  /** Called after this section changes the mode (Switch to INIT/INCREMENTAL),
   *  so the parent can re-read the PDO and surface the INIT rebuild banner. */
  onModeChanged?: () => void;
  /** Dry-run preview action (moved here from the retired Run section). */
  onPreview?: () => void | Promise<void>;
  previewBusy?: boolean;
  /** Run/preview result block rendered inside the State box (last-execution
   *  stats + merge analysis), supplied by the parent. */
  children?: React.ReactNode;
}) {
  const toast = useToast();
  const confirmCtx = useConfirm();
  const [state, setState] = useState<WatermarkState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<WatermarkAuditEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [showSetForm, setShowSetForm] = useState(false);
  const [setValueRaw, setSetValueRaw] = useState("");
  const [setReason, setSetReason] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<WatermarkState | { error: string; message?: string }>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/watermark`,
      );
      if ("error" in data) {
        // 404 / no binding — render nothing; the parent decides visibility.
        setState(null);
      } else {
        setState(data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [pdoId]);

  // `refreshNonce` in deps: the parent bumps it after a run so this section
  // re-reads its state (mode pill reflects the post-INIT flip to increment).
  useEffect(() => { void refresh(); }, [refresh, refreshNonce]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await apiFetch<{ entries: WatermarkAuditEntry[] }>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/watermark/audit`,
      );
      setHistory(data.entries);
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : String(e));
    } finally {
      setHistoryLoading(false);
    }
  }, [pdoId]);

  useEffect(() => {
    if (showHistory && history === null && !historyLoading) {
      void loadHistory();
    }
  }, [showHistory, history, historyLoading, loadHistory]);

  async function switchMode(target: "initialize" | "increment") {
    if (!state) return;
    if (target === "initialize") {
      const ok = await confirmCtx.confirm({
        title: "Switch to INIT mode",
        detail: "The next run of this persist will truncate all existing data and rebuild from scratch.",
        confirmLabel: "Switch to INIT",
        destructive: true,
        requireTypedConfirmation: { value: pdoName, label: "Type the data store name to confirm:" },
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const data = await apiFetch<WatermarkState>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/watermark/mode`,
        {
          method: "PATCH",
          // ADR-0102: operator-triggered mode flips carry reason='operator'
          // so the audit row + reinitialize_reason taxonomy can attribute
          // the rebuild correctly. Auto-flips back to increment carry NULL.
          body: target === "initialize" ? { mode: target, reason: "operator" } : { mode: target },
        },
      );
      setState(data);
      setHistory(null);
      // Mode changed server-side — let the parent re-read the PDO so the
      // INIT rebuild banner appears (or clears) to match.
      onModeChanged?.();
      toast.push({ kind: "success", message: `Mode set to ${target}.` });
    } catch (e) {
      toast.push({ kind: "error", message: `Mode change failed: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setBusy(false);
    }
  }

  async function submitSetValue() {
    if (!state) return;
    if (!setReason.trim()) {
      toast.push({ kind: "error", message: "Reason is required." });
      return;
    }
    setBusy(true);
    try {
      const data = await apiFetch<WatermarkState>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/watermark/value`,
        { method: "PATCH", body: { value: setValueRaw, reason: setReason.trim() } },
      );
      setState(data);
      setHistory(null);
      setShowSetForm(false);
      setSetValueRaw("");
      setSetReason("");
      toast.push({ kind: "success", message: "Watermark updated." });
    } catch (e) {
      toast.push({ kind: "error", message: `Set watermark failed: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="muted" style={{ fontSize: 12 }}>Loading watermark state…</div>;
  if (error) return <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>;
  if (!state) return null; // 404 / no binding — parent hides this section anyway.

  const isInit = state.mode === "initialize";
  const orphan = state.binding_orphaned;
  // Preview now runs the upstream providers for real (matches the next run),
  // so it's a genuine in-flight operation — gate the state-mutating actions on
  // it too, the same way they gate on a running materialization.
  const disableMostActions = busy || hasRunningMaterialization || orphan || Boolean(previewBusy);
  const disableSetWatermark = busy || hasRunningMaterialization || Boolean(previewBusy); // stays enabled when orphan
  const tooltipBusy = hasRunningMaterialization
    ? "Run in progress. Actions resume when the current run finishes."
    : previewBusy
      ? "Preview in progress. Actions resume when it finishes."
      : undefined;

  return (
    // minmax(0, 1fr): constrain children (incl. the embedded Run History) to the
    // card/frame width so wide content scrolls within its own control, never
    // widening the inspector.
    <div className="card" style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <div style={{ fontWeight: 600 }}>State</div>

      {orphan ? (
        <div
          style={{
            padding: "8px 10px",
            background: "var(--color-warn-bg, #fff7ed)",
            color: "var(--color-warn-text, #9a3412)",
            border: "1px solid var(--color-warn-border, #fdba74)",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          ⚠ Binding removed from operation. Re-save the operation to reconcile,
          or use <strong>Set watermark…</strong> to clear the orphaned state.
        </div>
      ) : null}

      {/* Mode chip — symmetric for both modes so the State box always shows
          the next-run mode. The top PersistRebuildBanner is the prominent
          "will rebuild" surface; this is the at-a-glance status in the State
          box itself. */}
      <div style={{ fontSize: 12 }}>
        {isInit ? (
          <span className="pill pillWarn" title="Next run truncates all data and rebuilds from scratch.">● INIT</span>
        ) : (
          <span className="pill pillOk" title="Next run loads new rows only.">● INCREMENTAL</span>
        )}
      </div>

      {/* State fields */}
      <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
        <Row label="Current watermark">
          <span className="mono">{state.current_value}</span>
          {isInit ? (
            <span className="muted" style={{ marginLeft: 6 }}>
              (will reset to initial-load value on next run)
            </span>
          ) : null}
        </Row>
        <Row label="Strategy">{orphan ? "(binding missing — re-save operation)" : strategyLabel(state.strategy)}</Row>
        <Row label="Next pull will be">
          <span className="mono" style={{ fontSize: 12 }}>{state.next_predicate_preview}</span>
        </Row>
        <Row label="Last successful run">
          <span>{state.last_advanced_at ? absoluteTime(state.last_advanced_at) : "—"}</span>
          {state.last_run_advance_error ? (
            <span
              style={{ marginLeft: 8, color: "var(--color-warn-text, #9a3412)" }}
              title={state.last_run_advance_error}
            >
              ⚠ advance failed
            </span>
          ) : null}
        </Row>
        <Row label="Last advance">
          {state.last_advance_from && state.last_advance_to
            ? <span className="mono" style={{ fontSize: 12 }}>{state.last_advance_from} → {state.last_advance_to}</span>
            : "—"}
        </Row>
      </div>

      {/* Action buttons */}
      <div className="actions" style={{ gap: 6, flexWrap: "wrap" }}>
        {isInit ? (
          <button
            className="btn"
            type="button"
            onClick={() => void switchMode("increment")}
            disabled={disableMostActions}
            title={tooltipBusy}
          >
            Switch to INCREMENTAL mode
          </button>
        ) : (
          <button
            className="btn btnDanger"
            type="button"
            onClick={() => void switchMode("initialize")}
            disabled={disableMostActions}
            title={tooltipBusy}
          >
            Switch to INIT mode
          </button>
        )}
        <button
          className="btn"
          type="button"
          onClick={() => setShowSetForm((s) => !s)}
          disabled={disableSetWatermark}
          title={tooltipBusy}
        >
          {showSetForm ? "Cancel" : "Set watermark…"}
        </button>
        <button
          className="btn btnPrimary"
          type="button"
          onClick={() => void onRunNow()}
          disabled={disableMostActions}
          title={tooltipBusy}
        >
          Run now
        </button>
        {onPreview ? (
          <button
            className="btn"
            type="button"
            onClick={() => void onPreview()}
            disabled={previewBusy || hasRunningMaterialization}
            title={tooltipBusy}
          >
            {previewBusy ? "Previewing…" : "Preview"}
          </button>
        ) : null}
      </div>

      {showSetForm ? (
        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div className="field">
            <label className="label">New value ({state.variable_type})</label>
            <input
              className="input"
              type={state.variable_type === "int" || state.variable_type === "decimal" ? "number" : state.variable_type === "date" ? "date" : state.variable_type === "datetime" ? "datetime-local" : "text"}
              value={setValueRaw}
              onChange={(e) => setSetValueRaw(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label">Reason (required, ≤ 200 chars)</label>
            <input
              className="input"
              value={setReason}
              maxLength={200}
              onChange={(e) => setSetReason(e.target.value)}
              placeholder="e.g., backfill before pipeline cutover"
            />
          </div>
          <div className="actions">
            <button className="btn btnPrimary" type="button" onClick={() => void submitSetValue()} disabled={busy}>
              {busy ? "Saving…" : "Set watermark"}
            </button>
          </div>
        </div>
      ) : null}

      {/* Run/preview results (last-execution stats + merge analysis), supplied
          by the parent now that the standalone Run section is retired. */}
      {children}

      <details
        open={showHistory}
        onToggle={(e) => setShowHistory((e.target as HTMLDetailsElement).open)}
      >
        <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--muted)" }}>
          History (last 100 entries)
        </summary>
        <div style={{ marginTop: 8 }}>
          {historyLoading ? <div className="muted" style={{ fontSize: 12 }}>Loading…</div> : null}
          {historyError ? <div className="help" style={{ color: "var(--danger-text)" }}>{historyError}</div> : null}
          {!historyLoading && !historyError && history?.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>No audit entries yet.</div>
          ) : null}
          {!historyLoading && history && history.length > 0 ? (
            <div className="tableWrap" style={{ maxHeight: 240, overflow: "auto" }}>
              <table className="table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((e) => (
                    <tr key={e.id}>
                      <td title={e.occurred_at}>{absoluteTime(e.occurred_at)}</td>
                      <td>{formatAuditKindLabel(e.kind)}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{formatAuditFromTo(e)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </details>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
      <span className="muted" style={{ fontSize: 12 }}>{label}</span>
      <span style={{ textAlign: "right" }}>{children}</span>
    </div>
  );
}
